import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const restoreUser = async () => {
      // If we have a saved user, restore it immediately so the UI doesn't
      // flash the login page on refresh. We'll validate the token (if any)
      // in the background and clear the session if validation fails.
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (e) {
          console.warn('AuthContext: failed to parse saved user', e);
          localStorage.removeItem('user');
        }
      }

      // If token exists, validate it by calling the profile endpoint.
      if (token) {
        try {
          const res = await api.get('/users/me');
          const profile = res.data;
          // Use profile from server (in case it's more up-to-date)
          setUser(profile);
          localStorage.setItem('user', JSON.stringify(profile));
        } catch (err) {
          // Token invalid/expired — clear storage and force logout
          console.warn('AuthContext: token validation failed', err?.message || err);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }

      setLoading(false);
    };

    restoreUser();

    // Listen for global auth failure events (dispatched by axios interceptor)
    const onUnauthorized = () => {
      // Clear storage and update state; this keeps logout logic centralized
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    };

    window.addEventListener('auth:unauthorized', onUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', onUnauthorized);
    };
  }, []);

  const login = async (email, password) => {
    try {
      console.log('AuthContext: Attempting login for', email);
      const response = await api.post('/auth/login', { email, password });
      console.log('AuthContext: Login response received', response.data);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      console.log('AuthContext: User state updated', user);
      return { success: true };
    } catch (error) {
      console.error('AuthContext: Login error', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const hasPermission = (module, action) => {
    if (!user || !user.role || !user.role.permissions) return false;
    
    const modulePermission = user.role.permissions.find(p => p.module === module);
    return modulePermission && modulePermission.actions.includes(action);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};
